import { useState } from 'react';
import { CATALOGOS, type TipoCatalogo } from '@copathy/shared';
import { ErrorApi } from '../lib/api';
import { useCatalogo, useGuardarOpcion } from '../lib/configuracion';
import { useClientes } from '../lib/clientes';
import { Boton } from '../componentes/Boton';
import { Campo } from '../componentes/Campo';
import { Combo } from '../componentes/Combo';
import { Lista } from '../componentes/iconos';

/**
 * Catálogos: las listas que alimentan los campos de selección.
 *
 * ── Una sola pantalla para todas ──
 * Criterio portado de Polaris: sumar una lista nueva cuesta una entrada en
 * `CATALOGOS` y **nada de interfaz**. Si cada catálogo tuviera su pantalla, la
 * quinta se haría distinto que la primera.
 */
export function PanelCatalogos() {
  const [tipo, setTipo] = useState<TipoCatalogo>('sector');
  const definicion = CATALOGOS.find((c) => c.tipo === tipo)!;

  const [clienteId, setClienteId] = useState<number | null>(null);
  const clientes = useClientes('', { activo: 'si' });

  return (
    <div className="grid gap-4 md:grid-cols-[16rem_1fr]">
      <nav className="h-fit space-y-1 rounded-2xl border border-marino-100 bg-white p-3">
        {CATALOGOS.map((c) => (
          <button
            key={c.tipo}
            onClick={() => setTipo(c.tipo)}
            className={`w-full rounded-xl px-3 py-2 text-left text-sm font-medium transition ${
              tipo === c.tipo
                ? 'bg-marino-800 text-white'
                : 'text-marino-500 hover:bg-marino-50 hover:text-marino-800'
            }`}
          >
            {c.titulo}
            {!c.global && (
              <span className="ml-1.5 text-xs font-normal opacity-70">por cliente</span>
            )}
          </button>
        ))}
      </nav>

      <section className="rounded-2xl border border-marino-100 bg-white p-5">
        <h2 className="text-base font-semibold text-marino-800">{definicion.titulo}</h2>
        <p className="mt-0.5 text-sm text-marino-400">{definicion.ayuda}</p>

        {/*
          Las áreas y los cargos son de CADA cliente: los de un cliente no tienen
          por qué parecerse a los de otro, y mezclarlos sería que uno vea la
          estructura interna del otro. Por eso hay que elegir cliente antes.
        */}
        {!definicion.global && (
          <div className="mt-4 max-w-sm">
            <Combo
              etiqueta="Cliente"
              opciones={(clientes.data?.datos ?? []).map((c) => ({ clave: c.id, texto: c.nombre }))}
              valor={clienteId}
              alCambiar={(v) => setClienteId(v === null ? null : Number(v))}
              vacio={clientes.isPending ? 'Cargando…' : 'Elige un cliente'}
              sinCoincidencias="Ningún cliente coincide."
            />
          </div>
        )}

        {definicion.global || clienteId !== null ? (
          <ListaOpciones
            tipo={tipo}
            singular={definicion.singular}
            {...(definicion.global ? {} : { empresaId: clienteId! })}
          />
        ) : (
          <p className="mt-6 rounded-xl border border-marino-100 bg-marino-50/60 px-4 py-6 text-center text-sm text-marino-500">
            Elige un cliente para ver sus {definicion.titulo.toLowerCase()}.
          </p>
        )}
      </section>
    </div>
  );
}

function ListaOpciones({
  tipo,
  singular,
  empresaId,
}: {
  tipo: TipoCatalogo;
  singular: string;
  empresaId?: number;
}) {
  // Acá SÍ se ven las inactivas: es la pantalla donde se administran.
  const consulta = useCatalogo(tipo, { empresaId, activo: 'todas' });
  const { crear, editar } = useGuardarOpcion();

  const [nueva, setNueva] = useState('');
  const [editando, setEditando] = useState<{ id: number; nombre: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function accion(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No pudimos completar la operación.');
    }
  }

  const lista = consulta.data ?? [];

  return (
    <div className="mt-5">
      {error && (
        <div
          role="alert"
          className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      <form
        className="mb-4 flex flex-wrap items-start gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (nueva.trim().length < 2) return;
          void accion(async () => {
            await crear.mutateAsync({
              tipo,
              nombre: nueva.trim(),
              ...(empresaId !== undefined ? { empresaId } : {}),
            });
            setNueva('');
          });
        }}
      >
        <div className="min-w-56 flex-1">
          <Campo
            etiqueta={`Agregar ${singular}`}
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
          />
        </div>
        <Boton type="submit" cargando={crear.isPending} disabled={nueva.trim().length < 2}>
          Agregar
        </Boton>
      </form>

      {consulta.isPending && <p className="py-8 text-center text-sm text-marino-400">Cargando…</p>}

      {lista.length === 0 && !consulta.isPending && (
        <div className="py-10 text-center">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-marino-50 text-marino-300">
            <Lista size={20} />
          </div>
          <p className="text-sm text-marino-500">Esta lista está vacía.</p>
        </div>
      )}

      <ul className="divide-y divide-marino-100">
        {lista.map((o) => (
          <li key={o.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3">
            {editando?.id === o.id ? (
              <>
                <div className="min-w-48 flex-1">
                  <Campo
                    etiqueta="Nombre"
                    value={editando.nombre}
                    onChange={(e) => setEditando({ id: o.id, nombre: e.target.value })}
                    autoFocus
                  />
                </div>
                <Boton
                  className="px-3.5 py-2 text-sm"
                  cargando={editar.isPending}
                  disabled={editando.nombre.trim().length < 2}
                  onClick={() =>
                    void accion(async () => {
                      await editar.mutateAsync({
                        id: o.id,
                        cambios: { nombre: editando.nombre.trim() },
                      });
                      setEditando(null);
                    })
                  }
                >
                  Guardar
                </Boton>
                <Boton
                  variante="texto"
                  className="px-2.5 py-2 text-sm"
                  onClick={() => setEditando(null)}
                >
                  Cancelar
                </Boton>
              </>
            ) : (
              <>
                <span
                  className={`min-w-0 flex-1 font-medium ${
                    o.activo ? 'text-marino-800' : 'text-marino-400 line-through'
                  }`}
                >
                  {o.nombre}
                </span>

                {/*
                  Cuántos la usan, siempre a la vista. Sin este número, desactivar
                  es a ciegas: quien lo hace no sabe si toca a nadie.
                */}
                {o.enUso > 0 && (
                  <span className="text-xs text-marino-400">
                    {o.enUso} {o.enUso === 1 ? 'registro la usa' : 'registros la usan'}
                  </span>
                )}
                {!o.activo && (
                  <span className="rounded-full bg-marino-50 px-2.5 py-0.5 text-xs font-semibold text-marino-500">
                    Inactiva
                  </span>
                )}

                <Boton
                  variante="texto"
                  className="px-2.5 py-1.5 text-sm"
                  onClick={() => setEditando({ id: o.id, nombre: o.nombre })}
                >
                  Renombrar
                </Boton>
                <Boton
                  variante="texto"
                  className="px-2.5 py-1.5 text-sm"
                  onClick={() =>
                    void accion(() =>
                      editar.mutateAsync({ id: o.id, cambios: { activo: !o.activo } }),
                    )
                  }
                >
                  {o.activo ? 'Desactivar' : 'Reactivar'}
                </Boton>
              </>
            )}
          </li>
        ))}
      </ul>

      {/*
        Nada se borra: se desactiva. Desactivar una opción EN USO se permite a
        propósito —es justo para lo que sirve, dejar de ofrecerla sin tocar lo que
        ya la usa—. Lo que ya la tenía conserva su valor, así que un informe viejo
        no cambia de forma porque alguien ordenó un menú.
      */}
      <p className="mt-5 rounded-xl border border-marino-100 bg-marino-50/60 px-4 py-3 text-xs text-marino-500">
        Nada se borra: se desactiva. Una opción desactivada deja de ofrecerse en los
        formularios, pero lo que ya la usaba la conserva — los informes viejos no cambian.
      </p>
    </div>
  );
}
